<template>
<div>
    <h1>Prix</h1>

    <div class="priceCalc">
      <div class="priceCalcServices">
        <div class="serviceCard" v-for="category in priceConfig.categories" :key="category.key">
          <div class="serviceTitle">{{ category.label }}</div>
          <div class="serviceOptions">
            <button
              type="button"
              class="optionChip"
              :class="{ active: selectedLabels[category.key] === option.label }"
              v-for="option in category.options"
              :key="`${category.key}-${option.label}`"
              @click="selectOption(category.key, option)"
            >
              <span>{{ option.label }}</span>
              <strong>{{ formatChf(option.amount) }}</strong>
            </button>
          </div>
        </div>
      </div>

      <div class="priceCalcSummary">
        <div class="summaryCard">
          <div class="summaryRow">
            <label for="otherServices">Autres services</label>
            <div class="p-inputgroup">
              <InputText id="otherServices" v-model="otherServices" />
              <span class="p-inputgroup-addon">CHF</span>
            </div>
          </div>
          <div class="quickAdd">
            <InputButton class="p-button-outlined p-button-secondary" @click="addOther(5)">+5</InputButton>
            <InputButton class="p-button-outlined p-button-secondary" @click="addOther(10)">+10</InputButton>
            <InputButton class="p-button-outlined p-button-secondary" @click="addOther(20)">+20</InputButton>
          </div>

          <div class="discountRow">
            <CheckBox inputId="priceDiscount" v-model="discountEnabled" :binary="true" />
            <label for="priceDiscount">Appliquer la réduction ({{ discountPercent }}%)</label>
          </div>

          <div class="totals">
            <div class="totalLine"><span>Sous-total</span><strong>{{ formatChf(subTotal) }}</strong></div>
            <div class="totalLine" v-if="discountEnabled"><span>Réduction</span><strong>- {{ formatChf(discountAmount) }}</strong></div>
            <div class="grandTotal"><span>Total</span><strong>{{ formatChf(total) }}</strong></div>
          </div>

          <div class="summaryActions">
            <InputButton class="p-button-outlined p-button-secondary" @click="clear">Effacer</InputButton>
            <InputButton class="p-button-success" @click="assignToAppointment">Facturer</InputButton>
          </div>
        </div>
      </div>
    </div>

    <DialogView v-model:visible="listApps" style="width:min(1100px, 96vw)">
      <GridLoader v-if="openAppointmentsLoading" class="loader"></GridLoader>
      <AppointmentListView :listItemName="itemName" :appointments="openAppointments" :lightVersion="true" :servicePrice="total" @app-updated="goToClient()"/>
    </DialogView>
</div>
</template>

<style scoped>
.priceCalc {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 330px;
  gap: 14px;
  align-items: start;
}

.priceCalcServices {
  display: grid;
  gap: 10px;
}

.serviceCard {
  border: 1px solid #dce5f4;
  border-radius: 12px;
  background: #fff;
  padding: 10px;
}

.serviceTitle {
  font-weight: 700;
  color: #284371;
  margin-bottom: 8px;
}

.serviceOptions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.optionChip {
  border: 1px solid #cfd9eb;
  background: #f8fbff;
  border-radius: 10px;
  padding: 10px 12px;
  min-height: 52px;
  min-width: 84px;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  cursor: pointer;
  color: #1c2b47;
}

.optionChip strong {
  font-size: 0.86rem;
  color: #45608f;
}

.optionChip.active {
  background: #2f5db8;
  border-color: #2f5db8;
  color: #fff;
}

.optionChip.active strong {
  color: #dfeaff;
}

.priceCalcSummary {
  position: sticky;
  top: 12px;
}

.summaryCard {
  border: 1px solid #dce5f4;
  border-radius: 12px;
  background: #fff;
  padding: 12px;
}

.summaryRow label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
}

.quickAdd {
  margin-top: 8px;
  display: flex;
  gap: 6px;
}

.discountRow {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.totals {
  margin-top: 12px;
  border-top: 1px solid #e1e8f5;
  padding-top: 10px;
}

.totalLine,
.grandTotal {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
}

.grandTotal {
  font-size: 1.2rem;
  font-weight: 700;
  color: #163062;
}

.summaryActions {
  margin-top: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

@media (max-width: 1200px) {
  .priceCalc {
    grid-template-columns: 1fr;
  }

  .priceCalcSummary {
    position: static;
  }
}
</style>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { authHeader } from '../helpers/authHeader.js';
import AppointmentListView from './AppointmentListView.vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const itemName = "rendez-vous ouvert";
const listApps = ref(false);

const openAppointmentsLoading = ref(false);
const openAppointments = ref([]);

const otherServices = ref("0");
const discountEnabled = ref(false);
const priceConfig = ref(defaultPriceConfig());
const selectedAmounts = ref({});
const selectedLabels = ref({});

onMounted(() => {
  loadPriceConfig();
});

const discountPercent = computed(() => toNumber(priceConfig.value.discountPercent, 10));

const subTotal = computed(() => {
  const selectedTotal = Object.values(selectedAmounts.value).reduce((sum, value) => sum + toNumber(value, 0), 0);
  return selectedTotal + toNumber(otherServices.value, 0);
});

const discountAmount = computed(() => {
  if (!discountEnabled.value) {
    return 0;
  }
  return subTotal.value * (discountPercent.value / 100);
});

const total = computed(() => roundToFiveCents(subTotal.value - discountAmount.value));

function defaultPriceConfig() {
  return {
    discountPercent: 10,
    categories: [
      { key: 'accueil', label: 'Forfait Accueil', options: [{ label: 'Non', amount: 0 }, { label: 'Oui', amount: 5 }] },
      { key: 'coupe', label: 'Forfait Coupe', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 20 }, { label: 'S', amount: 25 }, { label: 'M', amount: 45 }, { label: 'L', amount: 50 }, { label: 'XL', amount: 50 }] },
      { key: 'brushing', label: 'Forfait Brushing', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 10 }, { label: 'S', amount: 15 }, { label: 'M', amount: 20 }, { label: 'L', amount: 30 }, { label: 'XL', amount: 40 }] },
      { key: 'coloration', label: 'Forfait Coloration', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 67 }, { label: 'S', amount: 72 }, { label: 'M', amount: 94 }, { label: 'L', amount: 119 }, { label: 'XL', amount: 140 }] },
      { key: 'meches', label: 'Forfait Mèches', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 72 }, { label: 'S', amount: 78 }, { label: 'M', amount: 85 }, { label: 'L', amount: 125 }, { label: 'XL', amount: 145 }] },
      { key: 'balayage', label: 'Forfait Balayage', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 20 }, { label: 'S', amount: 20 }, { label: 'M', amount: 20 }, { label: 'L', amount: 25 }, { label: 'XL', amount: 25 }] },
      { key: 'lissage', label: 'Forfait Lissage', options: [{ label: 'Non', amount: 0 }, { label: 'S', amount: 112 }, { label: 'M', amount: 148 }, { label: 'L', amount: 170 }, { label: 'XL', amount: 180 }] },
      { key: 'permanente', label: 'Forfait Permanente', options: [{ label: 'Non', amount: 0 }, { label: 'S', amount: 90 }, { label: 'M', amount: 95 }, { label: 'L', amount: 110 }, { label: 'XL', amount: 135 }] },
      { key: 'gloss', label: 'Forfait Gloss', options: [{ label: 'Non', amount: 0 }, { label: 'XS', amount: 5 }, { label: 'S', amount: 5 }, { label: 'M', amount: 10 }, { label: 'L', amount: 15 }, { label: 'XL', amount: 20 }] }
    ]
  };
}

function loadPriceConfig() {
  const options = {
    headers: authHeader()
  };

  axios
    .get(`${process.env.VUE_APP_API_URL}/Settings`, options)
    .then((response) => {
      const settings = response.data || [];
      const configSetting = settings.find((s) => s.SettingName === 'priceCalculatorConfig');
      priceConfig.value = parsePriceConfig(configSetting?.SettingValue);
      resetSelections();
    })
    .catch(() => {
      priceConfig.value = defaultPriceConfig();
      resetSelections();
    });
}

function parsePriceConfig(rawValue) {
  const fallback = defaultPriceConfig();
  if (!rawValue) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!parsed || !Array.isArray(parsed.categories)) {
      return fallback;
    }
    const discount = toNumber(parsed.discountPercent, fallback.discountPercent);
    const categories = parsed.categories.map((category) => ({
      key: category.key,
      label: category.label || category.key,
      options: (category.options || []).map((option) => ({
        label: option.label,
        amount: toNumber(option.amount, 0)
      }))
    })).filter((category) => category.key && category.options.length > 0);

    if (!categories.length) {
      return fallback;
    }

    return {
      discountPercent: discount < 0 ? 0 : discount,
      categories
    };
  } catch {
    return fallback;
  }
}

function resetSelections() {
  const amounts = {};
  const labels = {};
  priceConfig.value.categories.forEach((category) => {
    const firstOption = category.options[0];
    amounts[category.key] = toNumber(firstOption?.amount, 0);
    labels[category.key] = firstOption?.label || '';
  });
  selectedAmounts.value = amounts;
  selectedLabels.value = labels;
}

function selectOption(categoryKey, option) {
  selectedAmounts.value = {
    ...selectedAmounts.value,
    [categoryKey]: toNumber(option.amount, 0)
  };
  selectedLabels.value = {
    ...selectedLabels.value,
    [categoryKey]: option.label
  };
}

function toNumber(value, fallback = 0) {
  const normalized = String(value ?? '').replace(',', '.').trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function roundToFiveCents(amount) {
  return Math.ceil(amount * 20) / 20;
}

function formatChf(amount) {
  return `${toNumber(amount, 0).toFixed(2)} CHF`;
}

function addOther(amount) {
  const next = toNumber(otherServices.value, 0) + amount;
  otherServices.value = next.toFixed(2);
}

function clear() {
  otherServices.value = "0";
  discountEnabled.value = false;
  resetSelections();
}

function goToClient() {
  router.push('/clients');
}

function assignToAppointment() {
  loadOpenAppointments();
  listApps.value = true;
}

function loadOpenAppointments() {
  openAppointmentsLoading.value = true;
  openAppointments.value = [];

  const options = {
    headers: authHeader()
  };

  axios
    .get(`${process.env.VUE_APP_API_URL}/Appointments/getOpen`, options)
    .then((response) => {
      openAppointments.value = response.data;
      openAppointmentsLoading.value = false;
    });
}
</script>
